import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import translations from './translations';
import { onPedidosArea, loadPedidosArea, areaOfKey } from './pedidos/register';

/* Pedidos online (COST-03): sus textos llegan con el pedazo de quien los usa (pedidos/register.js).
   Una clave de un área que todavía no está → se baja el área (una vez) y, cuando llega, se vuelve a
   pintar: nunca queda la clave a la vista para siempre aunque a un módulo le falte su cargador. */
function missingPedidosKey(key) {
  const area = areaOfKey(key);
  if (area) loadPedidosArea(area);
}

/* ──────────────────────────────────────────
   Supported locales
   ────────────────────────────────────────── */
const SUPPORTED_LOCALES = ['es', 'en', 'pt'];
const DEFAULT_LOCALE = 'es';
const STORAGE_KEY = 'restauradmin_ui_lang';

/* ──────────────────────────────────────────
   Detect browser locale → map to supported
   ────────────────────────────────────────── */
function detectLocale() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LOCALES.includes(stored)) return stored;
  } catch (_) { /* ignore */ }

  const browserLang = (navigator.language || navigator.userLanguage || '').toLowerCase();
  const short = browserLang.split('-')[0];
  if (SUPPORTED_LOCALES.includes(short)) return short;
  return DEFAULT_LOCALE;
}

/* ──────────────────────────────────────────
   Context
   ────────────────────────────────────────── */
const I18nContext = createContext(null);

/* ──────────────────────────────────────────
   Provider
   ────────────────────────────────────────── */
export function I18nProvider({ children, initialLocale = null, persist = true }) {
  // initialLocale + persist=false → para el widget público de reservas: arranca
  // en el idioma del RESTAURANTE (no el del navegador del visitante) y el cambio
  // del cliente no toca el idioma global del admin.
  const [locale, setLocaleState] = useState(() =>
    initialLocale && SUPPORTED_LOCALES.includes(initialLocale) ? initialLocale : detectLocale()
  );

  const setLocale = useCallback((lang) => {
    if (!SUPPORTED_LOCALES.includes(lang)) return;
    setLocaleState(lang);
    if (persist) {
      try { localStorage.setItem(STORAGE_KEY, lang); } catch (_) { /* ignore */ }
    }
  }, [persist]);

  // Sync lang attribute with document
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  // Llegó un área de pedidos online: `t` cambia y quien la usa vuelve a pintar con el texto.
  const [areasVersion, setAreasVersion] = useState(0);
  useEffect(() => onPedidosArea(() => setAreasVersion((v) => v + 1)), []);

  /**
   * t(key)            → plain string
   * t(key, { name })  → interpolate {name} in template
   */
  const t = useCallback((key, params) => {
    const dict = translations[locale] || translations[DEFAULT_LOCALE];
    let str = dict[key];
    if (str === undefined) {
      // fallback to default locale
      str = (translations[DEFAULT_LOCALE] || {})[key];
    }
    if (str === undefined) {
      missingPedidosKey(key);
      return key; // last resort: show the key itself
    }
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
      });
    }
    return str;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, areasVersion]);

  return (
    <I18nContext.Provider value={{ locale, setLocale, t, supportedLocales: SUPPORTED_LOCALES }}>
      {children}
    </I18nContext.Provider>
  );
}

/* ──────────────────────────────────────────
   Hook
   ────────────────────────────────────────── */
export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Graceful fallback so components that aren't wrapped still work
    return {
      locale: DEFAULT_LOCALE,
      setLocale: () => {},
      t: (key) => {
        const dict = translations[DEFAULT_LOCALE] || {};
        if (dict[key] === undefined) missingPedidosKey(key);
        return dict[key] || key;
      },
      supportedLocales: SUPPORTED_LOCALES,
    };
  }
  return ctx;
}

/* ──────────────────────────────────────────
   Language Switcher Component (inline styles)
   ────────────────────────────────────────── */
const FLAG_MAP = {
  es: '🇪🇸',
  en: '🇬🇧',
  pt: '🇧🇷',
};

const LABEL_MAP = {
  es: 'Español',
  en: 'English',
  pt: 'Português',
};

export function LanguageSwitcher({ style }) {
  const { locale, setLocale, supportedLocales } = useI18n();
  const [open, setOpen] = useState(false);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = () => setOpen(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [open]);

  const containerStyle = {
    position: 'relative',
    display: 'inline-block',
    ...style,
  };

  const btnStyle = {
    background: 'rgba(255,255,255,0.12)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 8,
    padding: '5px 10px',
    cursor: 'pointer',
    fontSize: 14,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    transition: 'background .15s',
  };

  const dropdownStyle = {
    position: 'absolute',
    top: '110%',
    right: 0,
    background: '#1e1e2e',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 10,
    overflow: 'hidden',
    zIndex: 1000,
    minWidth: 140,
    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
  };

  const itemStyle = (isActive) => ({
    padding: '8px 14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    color: isActive ? '#a78bfa' : '#e2e8f0',
    background: isActive ? 'rgba(167,139,250,0.12)' : 'transparent',
    transition: 'background .15s',
    fontWeight: isActive ? 600 : 400,
  });

  return (
    <div style={containerStyle}>
      <button
        style={btnStyle}
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
        title="Change language"
      >
        <span>{FLAG_MAP[locale] || '🌐'}</span>
        <span style={{ fontSize: 11 }}>▼</span>
      </button>

      {open && (
        <div style={dropdownStyle}>
          {supportedLocales.map((lang) => (
            <div
              key={lang}
              style={itemStyle(lang === locale)}
              onClick={() => { setLocale(lang); setOpen(false); }}
              onMouseEnter={(e) => { if (lang !== locale) e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
              onMouseLeave={(e) => { if (lang !== locale) e.currentTarget.style.background = 'transparent'; }}
            >
              <span>{FLAG_MAP[lang]}</span>
              <span>{LABEL_MAP[lang]}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { SUPPORTED_LOCALES, DEFAULT_LOCALE };
